// src/Dashboard/pages/user/PrepaPhysique/components/PrepaPhysiqueExerciseMobileCards.jsx

import React from "react";
import {
  FaExternalLinkAlt,
  FaImage,
  FaYoutube,
} from "react-icons/fa";

import {
  formatDateFR,
  getExerciseDate,
  getExerciseDone,
  getExerciseImage,
  getExerciseProgramme,
  getExerciseRepetitions,
  getExerciseVideo,
} from "../utils/prepaPhysiqueUserUtils";

function DoneSwitch({ checked, disabled, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-8 w-14 items-center rounded-full transition ${
        checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export default function PrepaPhysiqueExerciseMobileCards({
  exercises = [],
  adherentId,
  savingKey,
  onToggleDone,
  onOpenImage,
}) {
  return (
    <div className="space-y-4 lg:hidden">
      {exercises.map((exercise) => {
        const done = getExerciseDone(exercise);
        const repetitions = getExerciseRepetitions(exercise);
        const date = getExerciseDate(exercise);
        const programme = getExerciseProgramme(exercise);
        const video = getExerciseVideo(exercise);
        const image = getExerciseImage(exercise);
        const rowKey = `${adherentId}-${exercise.id}`;
        const saving = savingKey === rowKey;

        return (
          <article
            key={exercise.id}
            className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950"
          >
            <button
              type="button"
              onClick={() => onOpenImage(exercise)}
              className="block h-52 w-full bg-slate-100 dark:bg-slate-800"
              title="Agrandir l’illustration"
            >
              <img
                src={image}
                alt={exercise?.nom || "Illustration"}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = "/img/no-image.jpg";
                }}
              />
            </button>

            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">
                    {exercise?.nom || "Exercice"}
                  </h3>

                  {programme && (
                    <span className="mt-2 inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      {programme}
                    </span>
                  )}
                </div>

                <div className="shrink-0 text-right">
                  <DoneSwitch
                    checked={done}
                    disabled={saving}
                    onClick={() => onToggleDone(exercise, adherentId)}
                  />

                  <p
                    className={`mt-1 text-[11px] font-bold ${
                      done
                        ? "text-emerald-600 dark:text-emerald-300"
                        : "text-amber-600 dark:text-amber-300"
                    }`}
                  >
                    {saving ? "Sauvegarde…" : done ? "Réalisé" : "À faire"}
                  </p>
                </div>
              </div>

              {exercise?.description && (
                <div
                  className="mt-3 prose prose-sm max-w-none text-slate-700 dark:prose-invert dark:text-slate-200"
                  dangerouslySetInnerHTML={{ __html: exercise.description }}
                />
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                  Répétitions : {Number.isFinite(repetitions) ? repetitions : "—"}
                </span>

                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                  Date : {formatDateFR(date)}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onOpenImage(exercise)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-100 px-3 py-3 text-sm font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
                >
                  <FaImage />
                  Image
                </button>

                {video ? (
                  <a
                    href={video}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-50 px-3 py-3 text-sm font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                  >
                    <FaYoutube />
                    Vidéo
                    <FaExternalLinkAlt className="text-xs opacity-70" />
                  </a>
                ) : (
                  <div className="inline-flex items-center justify-center rounded-2xl bg-slate-50 px-3 py-3 text-sm font-bold text-slate-400 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800">
                    Pas de vidéo
                  </div>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}